import React, { useEffect, useCallback, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Typography } from "@mui/material";
import CircularProgress from "@material-ui/core/CircularProgress";
import { useSelector, useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import {
  selectCategories,
  Category,
  selectChildCategories,
  selectTopCategoriesFetched,
  selectTopCategories,
  selectIsCatalogOrStoreChanged,
} from "../../store/category/CategorySelectors";
import {
  LoadChildCategories,
  resetCategoryExpandState,
  resetTopCategoriesLoadingState,
  setCurrentCategory,
} from "../../store/category/CategoryActions";
import {
  getLocksInCategory,
  // loadProductsFromCategory,
  loadProductIdsFromCategory,
  resetLocksInCategory,
  getContentSlots,
  resetContentSlots,
  resetPinnedUnPinnedProducts,
  getPinnedProducts,
  resetProgressCache,
  loadInitialProducts,
  setCurrentPage,
  resetPositionedProductIds,
  resetPreview,
  setIsNewCategory,
  checkCategorySaveByAnotherUser,
} from "../../store/product-list/ProductListActions";
import { resetProducts } from "../../store/product/ProductActions";
import {
  selectProductSequenceMode,
  selectSavingCategoryIds,
} from "../../store/product-list/ProductListSelectors";
import { setModalState } from "../../store/modal/ModalActions";
import { ConfirmCategorySelectionModalId } from "./ConfirmCategorySelection";
import {
  getCategoryTooltip,
  isCategoryPublishedSFCC,
  getCategoryNameSFCC,
} from "../../utils/CategoryUtils";
import {
  selectCurrentStoreId,
  selectCurrentLocale,
  selectStoreListIds,
  selectCurrencyCode,
} from "../../store/store-list/StoreListSelectors";
import { selectCurrentCatalogId } from "../../store/catalog/CatalogSelectors";
import { useQuery } from "../../hooks/useQueryParams";
import {
  selectAccountFeatures,
  selectCallerAccountId,
  selectConfigValue,
} from "../../store/app-config/AppConfigSelectors";
import AppState from "../../store/AppState";
import TreeViewComponent, { TreeNode } from "../tree-view/TreeView";
import Tooltip from "../common/ToolTip";
import { resetClusterList } from "store/ai-cluster/AiClusterAction";
import { selectIsCategoryProductsV2Available } from "store/adapter-config/AdapterConfigSelectors";
import AppEnv from "utils/AppEnv";
import { fetchLinkedCategorySetByCategory } from "store/linked-categories/LinkedCategoriesActions";
import { fetchCategoryLatestsSync } from "store/sync-categories/SyncCategoriesActions";
import { resetProductBadgeState } from "store/product-badge/ProductBadgeActions";

const useStyles = makeStyles((theme) => ({
  root: {
    overflowX: "hidden",
    overflowY: "auto",
    flexGrow: 1,
    margin: theme.spacing(1),
  },
  rootLabel: {
    "&:focus>.MuiTreeItem-content .MuiTreeItem-label": {
      backgroundColor: "transparent !important",
    },
  },
  label: {
    display: "flex",
    alignItems: "center",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    "&:hover": {
      backgroundColor: "transparent !important",
      color: "#78a6be",
    },
  },
  count: {
    fontSize: ".8rem",
    marginLeft: "5px",
    marginBottom: "1px",
  },
  progress: {
    marginLeft: "5px",
  },
  skeletonLabel: {
    marginTop: 10,
    display: "flex",
    justifyContent: "center",
  },
  gearIcon: {
    marginRight: "2px",
  },
}));

interface Props {
  selected: string[];
  checked: boolean;
  setSelected: React.Dispatch<React.SetStateAction<string[]>>;
}

const CategorySelection: React.FC<Props> = (props) => {
  const { selected, setSelected } = props;
  const [expanded, setExpanded] = useState<string[]>([]);
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const url = location.pathname;
  const query = useQuery();
  const catalogIdQuery = query.get("catalogId") ?? "";
  const storeIdQuery = query.get("storeId") ?? "";
  const categoryPathQuery = query.get("categoryPath") ?? "";
  const localeCodeQuery = query.get("localeCode") ?? "";
  const localeCodeQueryStr =
    localeCodeQuery !== "" ? `&localeCode=${localeCodeQuery}` : "";
  const dispatch = useDispatch();

  const childCategories = useSelector(selectChildCategories);
  const sequenceMode = useSelector(selectProductSequenceMode);
  const savingCategoryIds = useSelector(selectSavingCategoryIds);
  const categoryTree = useSelector(selectCategories);
  const isInReportsTab = url.includes("reports");
  const currentStoreId = useSelector(selectCurrentStoreId) ?? "";
  const currentCatalogId = useSelector(selectCurrentCatalogId);
  const currentLocaleId = useSelector(selectCurrentLocale) ?? "";
  const isTopCategoriesFetched = useSelector(selectTopCategoriesFetched);
  const topCategories = useSelector(selectTopCategories);
  const storeListIds = useSelector(selectStoreListIds);
  const accountId = useSelector(selectCallerAccountId);
  const currencyCode = useSelector(selectCurrencyCode);
  const features = useSelector((state: AppState) =>
    selectAccountFeatures(state, accountId),
  );
  const isLockedProductsFeatureEnabled = features?.["locked-products"] ?? false;
  const isContentSlotsFeatureEnabled = features?.["content-slots"] ?? false;
  const isPinnedProductsFeatureEnabled = features?.["pinned-products"] ?? false;

  const defaultLocaleId = useSelector((state: AppState) =>
    selectConfigValue(state, "defaultLocaleId"),
  );
  const isCatalogOrStoreChanged = useSelector(selectIsCatalogOrStoreChanged);
  const userAccountId = useSelector(selectCallerAccountId);
  const isCategoryProductsV2Available = useSelector(
    selectIsCategoryProductsV2Available,
  );

  useEffect(() => {
    if (isCatalogOrStoreChanged) {
      setExpanded([]);
      dispatch(resetCategoryExpandState());
    }
    if (topCategories.length) {
      dispatch(resetTopCategoriesLoadingState());
    }
  }, [topCategories, dispatch, isCatalogOrStoreChanged]);

  const handleToggle = useCallback(
    (_event: object, node: Category | TreeNode) => {
      if (currentCatalogId) {
        const categoryId = node.id;
        if (!childCategories[categoryId]) {
          dispatch(
            LoadChildCategories(
              currentCatalogId,
              categoryId,
              currentLocaleId,
              currentStoreId,
            ),
          );
        }
      }
    },
    [
      childCategories,
      currentCatalogId,
      currentLocaleId,
      currentStoreId,
      dispatch,
    ],
  );
  const handleCategorySelection = useCallback(
    (categoryId: string) => {
      dispatch(checkCategorySaveByAnotherUser(categoryId));
      dispatch(setIsNewCategory());
      dispatch(resetClusterList());
      dispatch(resetProducts());
      dispatch(resetProductBadgeState());
      dispatch(resetProgressCache());
      dispatch(resetPositionedProductIds());
      dispatch(setCurrentPage(1));
      dispatch(resetPreview());
      const productPayload = {
        catalogId: currentCatalogId,
        categoryId: categoryId,
      };
      dispatch(fetchLinkedCategorySetByCategory(productPayload));
      dispatch(fetchCategoryLatestsSync(productPayload));
      if (isContentSlotsFeatureEnabled) {
        dispatch(resetContentSlots());
        dispatch(
          getContentSlots({
            ...productPayload,
            storeId: currentStoreId,
          }),
        );
      }
      //dispatch(loadProductsFromCategory(payload));
      if (isLockedProductsFeatureEnabled) {
        dispatch(resetLocksInCategory());
        dispatch(getLocksInCategory(productPayload));
      }
      if (isPinnedProductsFeatureEnabled) {
        dispatch(resetPinnedUnPinnedProducts());
        dispatch(getPinnedProducts(productPayload));
      }

      if (isCategoryProductsV2Available) {
        const payload = {
          categoryId,
          catalogId: currentCatalogId,
          localeCode: currentLocaleId,
          storeId: currentStoreId,
          page: 1,
          pageSize: AppEnv.PRODUCT_LIST_PAGE_SIZE ?? 24,
        };
        dispatch(loadInitialProducts(payload));
      } else {
        const payload = {
          categoryId,
          catalogId: currentCatalogId,
          localeCode: currentLocaleId,
          storeId: currentStoreId,
          storeListIds,
        };
        dispatch(loadProductIdsFromCategory(payload));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      currentCatalogId,
      currentLocaleId,
      currentStoreId,
      dispatch,
      storeListIds,
      accountId,
      currencyCode,
      isLockedProductsFeatureEnabled,
      isContentSlotsFeatureEnabled,
    ],
  );

  useEffect(
    () => {
      if (
        categoryPathQuery !== "" &&
        currentStoreId !== "" &&
        currentCatalogId !== ""
      ) {
        const categoryTreeQuery = categoryPathQuery.split(";");
        const selectedCategory =
          categoryTreeQuery[categoryTreeQuery.length - 1];
        if (
          selectedCategory &&
          selected.length &&
          selected[0] !== selectedCategory
        ) {
          const categoryPath = categoryTreeQuery
            ? `${categoryTreeQuery?.join(";")};${selected[0]}`
            : `${selected[0]}`;
          handleCategorySelection(selected[0]);
          setSelected(selected);
          dispatch(setCurrentCategory(selected[0]));
          categoryTreeQuery.pop();
          setExpanded(categoryTreeQuery);
          history.replace(
            `${url}?accountId=${userAccountId}&storeId=${storeIdQuery}${localeCodeQueryStr}&catalogId=${catalogIdQuery}&categoryPath=${categoryPath}`,
          );
        } else if (selected.length === 0) {
          handleCategorySelection(selectedCategory);
          setSelected([selectedCategory]);
          dispatch(setCurrentCategory(selectedCategory));
          categoryTreeQuery.pop();
          setExpanded(categoryTreeQuery);
        }
      } else {
        if (selected.length > 0) {
          setSelected([]);
        }
        if (
          (currentStoreId === "" || currentCatalogId === "") &&
          expanded.length > 0
        ) {
          setExpanded([]);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      categoryPathQuery,
      currentStoreId,
      currentCatalogId,
      selected,
      expanded,
      handleToggle,
      handleCategorySelection,
      setSelected,
      dispatch,
    ],
  );

  useEffect(() => {
    const categoryTreeQuery = categoryPathQuery.split(";");
    let CategoriesObj = categoryTree;
    const categoryPath: string[] = [];
    const newPath = categoryPath.join(";");
    categoryTreeQuery.forEach((id) => {
      if (CategoriesObj?.length === 0) return;

      const foundIndex = CategoriesObj.findIndex(
        (category) => category.id === id,
      );

      if (foundIndex !== -1) {
        categoryPath.push(id);
        CategoriesObj = CategoriesObj[foundIndex].children ?? [];
      } else if (newPath) {
        history.replace(
          `${url}?accountId=${userAccountId}&storeId=${storeIdQuery}${localeCodeQueryStr}&catalogId=${catalogIdQuery}&categoryPath=${newPath}`,
        );

        return;
      }
    });
  }, [
    localeCodeQueryStr,
    catalogIdQuery,
    categoryPathQuery,
    categoryTree,
    history,
    storeIdQuery,
    url,
    userAccountId,
  ]);

  const renderLabel = useCallback(
    (node: Category | TreeNode) => (
      <Tooltip tooltipTitle={getCategoryTooltip(node)} placement="right">
        <Typography variant="sidebarTreeTitle" noWrap>
          {isCategoryPublishedSFCC(node as Category) ? "" : "(U) "}
          {node?.isAutomatedCollection ? (
            <i className={`fa-solid fa-gears ${classes.gearIcon}`} />
          ) : (
            ""
          )}
          {getCategoryNameSFCC(
            node as Category,
            currentLocaleId,
            defaultLocaleId,
          )}
          {node ? (
            <span className={classes.count}>
              {node.isProductCountLoading ? (
                <CircularProgress size={15} className={classes.progress} />
              ) : node.productCount ? (
                `(${node.productCount.toString()})`
              ) : (
                ""
              )}
            </span>
          ) : (
            <CircularProgress size={15} className={classes.progress} />
          )}
        </Typography>
      </Tooltip>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [classes.count, classes.progress, currentLocaleId, defaultLocaleId],
  );
  const labelClickHandler = useCallback(
    (node: Category | TreeNode, e: React.MouseEvent) => {
      e.preventDefault();
      // Get current category ID from selected array
      const currentCategoryId = selected.length > 0 ? selected[0] : "";
      // Don't show warning if current category is being saved
      const isCurrentCategorySaving = savingCategoryIds.includes(currentCategoryId);
      if (!isInReportsTab && sequenceMode && !isCurrentCategorySaving) {
        dispatch(
          setModalState(ConfirmCategorySelectionModalId, true, {
            categoryId: node.id,
            categoryPath: node.categoryPath,
          }),
        );
      } else {
        handleCategorySelection(node.id);
        setSelected([node.id]);
        dispatch(setCurrentCategory(node.id));
        const categoryPath = node.categoryPath
          ? `${node.categoryIds?.join(";")};${node.id}`
          : `${node.id}`;

        history.push(
          `${url}?accountId=${userAccountId}&storeId=${storeIdQuery}${localeCodeQueryStr}&catalogId=${catalogIdQuery}&categoryPath=${categoryPath}`,
        );
      }
    },
    [
      localeCodeQueryStr,
      catalogIdQuery,
      dispatch,
      sequenceMode,
      history,
      setSelected,
      storeIdQuery,
      url,
      handleCategorySelection,
      isInReportsTab,
      userAccountId,
      selected,
      savingCategoryIds,
    ],
  );

  return storeIdQuery !== "" ? (
    <TreeViewComponent
      nodes={categoryTree}
      selectedNodes={selected}
      handleToggle={handleToggle}
      labelClickHandler={labelClickHandler}
      renderLabel={renderLabel}
      showInitialLoading={categoryTree.length === 0 && !isTopCategoriesFetched}
      expanded={expanded}
    />
  ) : null;
};

export default CategorySelection;
